import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import certificateService from "../../api/services/certificate";
import {
  FaShoppingBag,
  FaCheck,
  FaPlay,
  FaClock,
  FaCalendar,
  FaCertificate,
  FaHourglassHalf,
  FaCheckCircle,
  FaFileAlt,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  selectCartItems,
  selectCartLoading,
} from "../../store/slices/cart/slice";
import { addToCart } from "../../store/slices/cart/asyncThunks";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import { selectUserCourses } from "../../store/slices/courses/slice";
import { getUserCourses } from "../../store/slices/courses/asyncThunks";

// Lesson progress imports
import { loadCourseLessons } from "../../store/slices/lessonProgress/asyncThunks";
import {
  Card,
  CardContent,
  Title,
  Description,
  Stats,
  Stat,
  Price,
  CTAButton,
  Subtitle,
  CardImageWrapper,
  ImageStatusBadge,
} from "./CourseCard.styles";
import {
  CourseProgress,
  ProgressBar,
  ProgressFill,
  ProgressText,
} from "../../screens/User/MyCoursesPage/MyCoursesPage.styles";
import { priceFormatter } from "../../utils/utils";
import { BASE_URL } from "../../api";

const CourseCard = ({
  course,
  variant = "default",
  animate = false,
  offMaxWidth = false,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux selectors
  const cartItems = useSelector(selectCartItems);
  const cartLoading = useSelector(selectCartLoading);
  const loggedIn = useSelector(selectLoggedIn);
  const userCourses = useSelector(selectUserCourses);

  // Check if this course is purchased
  const isPurchased =
    loggedIn &&
    userCourses.some(
      (uc) => uc.courseId === course.id || uc.course?.id === course.id,
    );

  // Fetch user courses if logged in and not yet loaded
  useEffect(() => {
    if (loggedIn && userCourses.length === 0 && variant !== "mycourses") {
      dispatch(getUserCourses());
    }
  }, [loggedIn, dispatch, userCourses.length, variant]);

  // Local state
  const [addedItems, setAddedItems] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [certificateStatus, setCertificateStatus] = useState(null);
  const [certificateStatusLoaded, setCertificateStatusLoaded] = useState(false);

  // Derived course state for "mycourses" variant
  const isCompleted = (course.progress ?? 0) >= 100;
  const isExpired =
    Boolean(course.expiryDate) && new Date(course.expiryDate) < new Date();

  // Broj dana do isteka pristupa (null = doživotan pristup)
  const daysUntilExpiry = course.expiryDate
    ? Math.ceil(
        (new Date(course.expiryDate) - new Date()) / (1000 * 60 * 60 * 24),
      )
    : null;
  const isExpiringSoon =
    daysUntilExpiry !== null && daysUntilExpiry > 0 && daysUntilExpiry <= 30;

  // Load certificate status for completed courses in mycourses variant
  useEffect(() => {
    const loadCertificateStatus = async () => {
      if (variant === "mycourses" && course.progress >= 100) {
        try {
          const response = await certificateService.getCertificateStatus(
            course.id,
          );
          setCertificateStatus(response);
          setCertificateStatusLoaded(true);
        } catch (error) {
          // Status nepoznat — ne prikazuj "Možete aplicirati" banner
          console.error("Error loading certificate status:", error);
        }
      }
    };

    loadCertificateStatus();
  }, [variant, course.id, course.progress]);

  // Helper functions
  const isInCart = (courseId) => {
    return cartItems.some((item) => item.courseId === courseId);
  };

  // Handle add to cart
  const handleAddToCart = (courseId) => {
    dispatch(addToCart({ courseId }));

    setAddedItems((prev) => new Set([...prev, courseId]));
    setTimeout(() => {
      setAddedItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(courseId);
        return newSet;
      });
    }, 2000);
  };

  // Handle navigation to course lesson using new Redux system
  const handleStartCourse = async () => {
    try {
      setLoading(true);

      // Load course lessons with progress using Redux
      const result = await dispatch(loadCourseLessons(course.id)).unwrap();
      const lessons = result.lessons || [];

      if (lessons.length === 0) {
        toast.info("Kurs je u razvoju - lekcije će uskoro biti dostupne");
        return;
      }

      // Find the lesson where user should continue
      let targetLesson = null;

      // Strategy:
      // 1. Find lesson with most progress (not completed)
      // 2. If none, find first unlocked lesson
      // 3. If all completed, go to last lesson

      // 1. Find lessons with progress (watchedDuration > 0, not completed)
      const lessonsWithProgress = lessons.filter(
        (lesson) =>
          lesson.isUnlocked &&
          !lesson.progress?.isCompleted &&
          (lesson.progress?.watchedDuration || 0) > 0,
      );

      if (lessonsWithProgress.length > 0) {
        // Get lesson with highest watchedDuration
        targetLesson = lessonsWithProgress.reduce((latest, current) => {
          const latestDuration = latest.progress?.watchedDuration || 0;
          const currentDuration = current.progress?.watchedDuration || 0;
          return currentDuration > latestDuration ? current : latest;
        });
      }

      // 2. If no progress, find first unlocked lesson
      if (!targetLesson) {
        targetLesson = lessons.find((lesson) => lesson.isUnlocked);
      }

      // 3. If all completed, go to last lesson
      if (!targetLesson) {
        targetLesson = lessons[lessons.length - 1];
      }

      // Navigate to the target lesson using new video page route
      if (targetLesson) {
        navigate(
          `/korisnicki-panel/kursevi/${course.id}/lekcije/${targetLesson.id}`,
        );
      } else {
        // Fallback to course list if no lessons found
        toast.info("Kurs je u razvoju - lekcije će uskoro biti dostupne");
        navigate(`/korisnicki-panel/kursevi`);
      }
    } catch (error) {
      console.error("Error loading course lessons:", error);
      const errorMessage = typeof error === "string" ? error : error?.message;
      toast.error(errorMessage || "Greška prilikom učitavanja lekcija");
      // Fallback navigation to course list
      navigate(`/korisnicki-panel/kursevi`);
    } finally {
      setLoading(false);
    }
  };

  // Download approved certificate PDF
  const [downloadingCert, setDownloadingCert] = useState(false);
  const handleDownloadCertificate = async () => {
    const applicationId = certificateStatus?.application?.id;
    if (!applicationId || downloadingCert) return;

    setDownloadingCert(true);
    try {
      await certificateService.downloadCertificate(applicationId);
    } catch (error) {
      toast.error("Greška pri preuzimanju sertifikata");
    } finally {
      setDownloadingCert(false);
    }
  };

  // Handle renewal of expired course access
  const handleRenewAccess = () => {
    navigate(`/kurs/${course.slug || course.id}`);
    setTimeout(() => {
      window.scroll(0, 0);
    }, 300);
  };

  const materials = course?.materialsIncluded
    ? course?.materialsIncluded.split(",").map((item) => item.trim())
    : ["", "", "", ""];

  // Different content for mycourses variant
  if (variant === "mycourses") {
    return (
      <Card $animate={animate} $variant={variant}>
        <CardImageWrapper>
          <img
            src={`${BASE_URL}/uploads/courses-thumbnails/${course.thumbnailImage}`}
            alt={`${course.title} thumbnail`}
          />

          {/* Status preko slike — ne menja visinu kartice */}
          {isExpired ? (
            <ImageStatusBadge $variant="expired">
              <FaExclamationTriangle />
              Pristup istekao
            </ImageStatusBadge>
          ) : certificateStatus?.status === "pending" ? (
            <ImageStatusBadge $variant="pending">
              <FaHourglassHalf />
              Sertifikat na čekanju
            </ImageStatusBadge>
          ) : certificateStatus?.status === "approved" ? (
            <ImageStatusBadge $variant="approved">
              <FaCheckCircle />
              Sertifikat odobren
              <button
                type="button"
                onClick={handleDownloadCertificate}
                disabled={downloadingCert}
                style={{
                  background: "none",
                  border: "none",
                  color: "inherit",
                  fontFamily: "inherit",
                  fontSize: "inherit",
                  fontWeight: 700,
                  textDecoration: "underline",
                  cursor: downloadingCert ? "wait" : "pointer",
                  padding: 0,
                }}
              >
                {downloadingCert ? "Preuzimam..." : "Preuzmi"}
              </button>
            </ImageStatusBadge>
          ) : certificateStatus?.status === "rejected" ? (
            <ImageStatusBadge $variant="info">
              <FaCertificate />
              Možete aplicirati ponovo
            </ImageStatusBadge>
          ) : course.progress >= 100 &&
            certificateStatusLoaded &&
            !certificateStatus?.hasApplication ? (
            <ImageStatusBadge $variant="info">
              <FaCertificate />
              Možete aplicirati za sertifikat
            </ImageStatusBadge>
          ) : null}
        </CardImageWrapper>

        <CardContent>
          <Title>{course.title}</Title>
          <Subtitle>Online edukacija</Subtitle>
          <Description>
            {course.shortDescription || course.description}
          </Description>
          <Stats>
            {materials[0] && (
              <Stat>
                <FaClock />
                <span>{materials[0]}</span>
              </Stat>
            )}

            {materials[1] && (
              <Stat>
                <FaPlay />
                <span>{materials[1]}</span>
              </Stat>
            )}

            {materials[2] && (
              <Stat>
                <FaFileAlt />
                <span>{materials[2]}</span>
              </Stat>
            )}

            {/* Stvarni datum isteka pristupa umesto statičnog teksta */}
            {course.expiryDate && !isExpired ? (
              <Stat
                style={
                  isExpiringSoon
                    ? { color: "#b45309", fontWeight: 600 }
                    : undefined
                }
              >
                {isExpiringSoon ? <FaExclamationTriangle /> : <FaCalendar />}
                <span style={isExpiringSoon ? { color: "#b45309" } : undefined}>
                  {isExpiringSoon
                    ? `Pristup ističe za ${daysUntilExpiry} ${
                        daysUntilExpiry === 1 ? "dan" : "dana"
                      }`
                    : `Pristup do ${new Date(
                        course.expiryDate,
                      ).toLocaleDateString("sr-Latn-RS")}`}
                </span>
              </Stat>
            ) : (
              materials[3] && (
                <Stat>
                  <FaCalendar />
                  <span>{materials[3]}</span>
                </Stat>
              )
            )}
          </Stats>

          {/* Course progress — marginTop: auto gura progres + dugme zajedno
              na dno kartice, pa višak prostora ostaje iznad njih */}
          <CourseProgress style={{ marginBottom: 0, marginTop: "auto" }}>
            <ProgressBar>
              <ProgressFill
                style={{
                  width: `${Math.min(Math.max(course.progress ?? 0, 0), 100)}%`,
                }}
              />
            </ProgressBar>
            <ProgressText>{course.progress ?? 0}% završeno</ProgressText>
            {course.lastAccessedAt && (
              <Stat style={{ marginTop: "8px" }}>
                <FaCalendar />
                <span>
                  Poslednji pristup:{" "}
                  {new Date(course.lastAccessedAt).toLocaleDateString(
                    "sr-Latn-RS",
                  )}
                </span>
              </Stat>
            )}
          </CourseProgress>

          {isExpired ? (
            <CTAButton className="mycourses-btn" onClick={handleRenewAccess}>
              <FaShoppingBag />
              Obnovi Pristup
            </CTAButton>
          ) : (
            <CTAButton
              className="mycourses-btn"
              onClick={handleStartCourse}
              disabled={loading}
            >
              <FaPlay />
              {loading
                ? "Učitavanje..."
                : isCompleted
                  ? "Pogledaj Ponovo"
                  : course.hasStarted || course.progress > 0
                    ? "Nastavi Kurs"
                    : "Počni Kurs"}
            </CTAButton>
          )}
        </CardContent>
      </Card>
    );
  }

  // Handle navigation to course details
  const handleCourseClick = () => {
    if (isPurchased) {
      handleStartCourse();
      return;
    }
    navigate(`/kurs/${course.slug || course.id}`);
    setTimeout(() => {
      window.scroll(0, 0);
    }, 300);
  };

  return (
    <Card
      $animate={animate}
      $variant={variant}
      onClick={handleCourseClick}
      $click
      $offMaxWidth={offMaxWidth}
    >
      <CardImageWrapper $variant={variant}>
        <img
          src={`${BASE_URL}/uploads/courses-thumbnails/${course.thumbnailImage}`}
          alt={`${course.title} thumbnail`}
        />
      </CardImageWrapper>

      <CardContent $variant={variant}>
        <Title>{course.title}</Title>
        <Subtitle>Online edukacija</Subtitle>

        <Description>
          {course.shortDescription || course.description}
        </Description>

        <Stats $variant={variant}>
          {materials[0] && (
            <Stat>
              <FaClock />
              <span>{materials[0]}</span>
            </Stat>
          )}

          {materials[1] && (
            <Stat>
              <FaPlay />
              <span>{materials[1]}</span>
            </Stat>
          )}

          {materials[2] && (
            <Stat>
              <FaFileAlt />
              <span>{materials[2]}</span>
            </Stat>
          )}

          {materials[3] && (
            <Stat>
              <FaCalendar />
              <span>{materials[3]}</span>
            </Stat>
          )}
        </Stats>

        <Price>
          {course.discountPrice && (
            <span className="original-price">
              {priceFormatter(course.price)} RSD
            </span>
          )}
          <span className="current-price">
            {priceFormatter(course.discountPrice || course.price)} RSD
          </span>
        </Price>

        {isPurchased ? (
          <CTAButton
            onClick={(e) => {
              e.stopPropagation();
              handleStartCourse();
            }}
            disabled={loading}
            className="added"
          >
            <FaPlay />
            {loading ? "Učitavanje..." : "Nastavi sa gledanjem"}
          </CTAButton>
        ) : (
          <CTAButton
            onClick={(e) => {
              e.stopPropagation();
              handleAddToCart(course.id);
            }}
            disabled={cartLoading}
            className={
              addedItems.has(course.id) || isInCart(course.id) ? "added" : ""
            }
          >
            {addedItems.has(course.id) ? (
              <>
                <FaCheck />
                Dodato!
              </>
            ) : isInCart(course.id) ? (
              <>
                <FaCheck />U Korpi
              </>
            ) : (
              <>
                <FaShoppingBag />
                Dodaj u Korpu
              </>
            )}
          </CTAButton>
        )}
      </CardContent>
    </Card>
  );
};

export default CourseCard;
